import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Power, Clock, ShieldAlert, Crown, HardHat, LogOut, KeyRound, Pencil, Briefcase, AlarmClock, BellOff, BellRing, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, priceLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NotificationSetup } from "@/components/InstallHelp";
import { RequestRow } from "@/pages/MyRequests";

const STATUS_MSG = {
  pending: ["Cadastro em análise", "Assim que o administrador aprovar, você poderá ficar disponível e receber pedidos.", "bg-yellow-50 text-yellow-900"],
  rejected: ["Cadastro reprovado", "Revise seus dados e salve de novo para uma nova análise.", "bg-red-50 text-red-800"],
  blocked: ["Cadastro bloqueado", "Fale com o suporte para saber mais.", "bg-red-50 text-red-800"],
};

function AvailabilityToggle({ provider, onChange }) {
  const [busy, setBusy] = useState(false);
  const on = provider.available;
  const toggle = async () => {
    setBusy(true);
    try { await api.post("/provider/availability", { available: !on }); await onChange(); toast.success(!on ? "Você está disponível!" : "Você está indisponível."); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <button onClick={toggle} disabled={busy} role="switch" aria-checked={!!on} data-testid="provider-toggle-available"
      className={`block w-full rounded-3xl border-4 p-5 text-left text-white shadow-lg transition-colors disabled:opacity-70 ${on ? "border-green-700 bg-green-600 hover:bg-green-700" : "border-orange-700 bg-orange-600 hover:bg-orange-700"}`}>
      <span className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <span className="min-w-0 flex-1 font-display text-2xl font-extrabold leading-tight" data-testid="provider-availability-title">
          Você está {on ? "DISPONÍVEL" : "INDISPONÍVEL"}
        </span>
      <span className={`relative h-14 w-[150px] shrink-0 rounded-full border-2 border-white/80 transition-colors ${on ? "bg-green-800" : "bg-slate-700"}`} data-testid="provider-availability-switch">
        <span className={`absolute inset-y-0 flex items-center justify-center text-sm font-extrabold tracking-wide ${on ? "left-1 right-[3.1rem]" : "left-[3.1rem] right-1"}`}>
          {on ? "LIGADO" : "DESLIGADO"}
        </span>
        <span className={`absolute top-1 h-11 w-11 rounded-full bg-white shadow-md transition-all duration-300 ${on ? "left-[calc(100%-2.95rem)]" : "left-1"}`} />
      </span>
      </span>
      <span className="mt-3 block text-lg font-medium leading-snug" data-testid="provider-availability-text">
        {on ? "Você aparece no mapa e recebe pedidos. Toque para ficar indisponível." : "Você NÃO aparece no mapa e NÃO recebe pedidos. Toque para ficar disponível."}
      </span>
    </button>
  );
}

function PushAlert() {
  const { data } = useQuery({ queryKey: ["push-status"], queryFn: () => api.get("/push/status").then((r) => r.data), refetchInterval: 30000 });
  if (!data || data.active) return null;
  return (
    <a href="#notification-setup" className="block rounded-3xl border-2 border-red-300 bg-red-50 p-5 text-red-900" data-testid="push-inactive-alert">
      <p className="flex items-center gap-2 text-xl font-bold"><BellOff className="h-7 w-7" /> Suas notificações estão desligadas</p>
      <p className="mt-1 text-lg">Você pode perder pedidos. Toque em <b>Ativar notificações</b> logo abaixo para ser avisado na hora, mesmo com o site fechado.</p>
    </a>
  );
}

function PendingOffers() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: offers = [] } = useQuery({ queryKey: ["offers"], queryFn: () => api.get("/provider/offers").then((r) => r.data), refetchInterval: 4000 });
  const act = async (id, kind) => {
    try {
      await api.post(`/requests/${id}/${kind}`);
      if (kind === "accept") { toast.success("Pedido aceito! Fale com o cliente pelo WhatsApp."); navigate(`/pedido/${id}`); }
      else toast.success("Pedido recusado. Vamos oferecer a outro prestador.");
    } catch (e) { toast.error(errMsg(e)); if (e.response?.status === 402) navigate("/plano-pro"); }
    qc.invalidateQueries({ queryKey: ["offers"] }); qc.invalidateQueries({ queryKey: ["jobs"] });
  };
  return (
    <div className={`po-card ${offers.length ? "po-flash border-4" : ""}`} data-testid="provider-pending-list">
      <p className="flex items-center gap-2 text-xl font-bold text-slate-900"><BellRing className="h-6 w-6 text-orange-500" /> Pedidos pendentes</p>
      {offers.length === 0 && <p className="mt-2 text-lg text-slate-600" data-testid="provider-pending-empty">Nenhum pedido esperando sua resposta agora.</p>}
      <div className="mt-3 space-y-3">
        {offers.map((o) => (
          <div key={o.id} className="rounded-2xl bg-orange-50 p-4" data-testid={`pending-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-lg font-bold text-slate-900">{o.service_name}</p>
              <Countdown expiresAt={o.expires_at} />
            </div>
            <p className="text-base text-slate-700">{o.neighborhood}, {o.city} · {priceLabel(o)}</p>
            {o.description && <p className="mt-1 text-base text-slate-600">{o.description}</p>}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={() => act(o.id, "accept")} className="po-btn-green" data-testid={`pending-accept-${o.id}`}><Check className="h-5 w-5" /> Aceitar</button>
              <button onClick={() => act(o.id, "decline")} className="po-btn-outline" data-testid={`pending-decline-${o.id}`}><X className="h-5 w-5" /> Recusar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Countdown({ expiresAt }) {
  const [, setT] = useState(0);
  useEffect(() => { const i = setInterval(() => setT((x) => x + 1), 1000); return () => clearInterval(i); }, []);
  const left = Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000));
  return <span className="rounded-full bg-orange-500 px-3 py-1 font-bold tabular-nums text-white">{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</span>;
}

function MissedOffers() {
  const qc = useQueryClient();
  const { data: missed = [] } = useQuery({ queryKey: ["missed"], queryFn: () => api.get("/provider/missed").then((r) => r.data), refetchInterval: 15000 });
  const accept = async (id) => {
    try {
      await api.post(`/requests/${id}/accept`);
      toast.success("Pedido aceito! O cliente foi avisado.");
      qc.invalidateQueries({ queryKey: ["missed"] }); qc.invalidateQueries({ queryKey: ["jobs"] });
    } catch (e) { toast.error(errMsg(e)); qc.invalidateQueries({ queryKey: ["missed"] }); }
  };
  if (!missed.length) return null;
  return (
    <div className="po-card border-2 border-orange-300" data-testid="provider-missed-list">
      <p className="flex items-center gap-2 text-xl font-bold text-slate-900"><AlarmClock className="h-6 w-6 text-orange-500" /> Pedidos que você perdeu</p>
      <p className="mt-1 text-base text-slate-600">O tempo para aceitar acabou, mas ninguém pegou ainda. Você pode aceitar por até 24 horas.</p>
      <div className="mt-3 space-y-3">
        {missed.map((r) => (
          <div key={r.id} className="rounded-2xl bg-orange-50 p-4" data-testid={`missed-${r.id}`}>
            <p className="text-lg font-bold text-slate-900">{r.service_name}</p>
            <p className="text-base text-slate-700">{r.neighborhood}, {r.city} · {priceLabel(r)}</p>
            {r.description && <p className="mt-1 text-base text-slate-600">{r.description}</p>}
            <button onClick={() => accept(r.id)} className="po-btn-primary mt-3 w-full" data-testid={`missed-accept-${r.id}`}>Aceitar agora</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DiscountControl({ provider, onSaved }) {
  const saved = Number(provider.discount_pct) || 0;
  const [val, setVal] = useState(saved);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/provider/discount", { discount_pct: val });
      toast.success(val > 0 ? `Desconto de ${val}% salvo.` : "Desconto removido.");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="po-card" data-testid="provider-discount-card">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor="discount-range" className="text-xl font-bold text-slate-900">Desconto no orçamento</label>
        <span className={`rounded-full px-3 py-1 text-xl font-extrabold tabular-nums ${val > 0 ? "bg-green-600 text-white" : "bg-slate-100 text-slate-700"}`} data-testid="provider-discount-value">{val}%</span>
      </div>
      <input id="discount-range" type="range" min={0} max={60} step={1} value={val} onChange={(e) => setVal(Number(e.target.value))}
        className="mt-4 h-10 w-full cursor-pointer accent-green-600" data-testid="provider-discount-slider" aria-valuetext={`${val}%`} />
      <div className="flex justify-between text-sm text-slate-500"><span>0%</span><span>60%</span></div>
      <p className="mt-3 rounded-2xl bg-amber-50 p-3 text-base font-medium text-amber-900" data-testid="provider-discount-warning">
        Ao ativar, você se compromete a honrar esse desconto com o cliente.
      </p>
      <button onClick={save} disabled={busy || val === saved} className="po-btn-primary mt-4 w-full" data-testid="provider-discount-save">
        {busy ? "Salvando..." : "Salvar"}
      </button>
    </div>
  );
}

const emptyPrice = () => ({ name: "", value: "", highlight: false });

function PricesEditor({ provider, onSaved }) {
  const [items, setItems] = useState(() => (provider.prices || []).map((x) => ({ ...x, value: String(x.value).replace(".", ",") })));
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setItems((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : patch.highlight ? { ...x, highlight: false } : x)));
  const save = async () => {
    const prices = items.filter((x) => x.name.trim() || x.value).map((x) => ({ ...x, name: x.name.trim(), value: Number(String(x.value).includes(",") ? String(x.value).replace(/\./g, "").replace(",", ".") : x.value) }));
    if (prices.some((x) => !x.name || !(x.value > 0))) return toast.error("Preencha o nome e o valor de cada preço.");
    setBusy(true);
    try { await api.put("/provider/prices", { prices }); toast.success("Preços salvos."); onSaved(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="po-card" data-testid="provider-prices-card">
      <p className="text-xl font-bold text-slate-900">Meus preços</p>
      <p className="text-base text-slate-600">Opcional. Até 5 itens. O preço destaque aparece no seu card.</p>
      <div className="mt-3 space-y-3">
        {items.map((x, i) => (
          <div key={i} className="relative rounded-2xl border-2 border-slate-200 p-3 pt-9" data-testid={`price-row-${i}`}>
            <button type="button" onClick={() => setItems((l) => l.filter((_, j) => j !== i))} className="absolute right-2 top-1.5 min-h-[32px] px-2 text-sm font-semibold text-red-600 underline" data-testid={`price-remove-${i}`}>Remover</button>
            <input className="po-input min-h-[56px] w-full text-lg" placeholder="Ex.: Diária" maxLength={40} value={x.name} onChange={(e) => set(i, { name: e.target.value })} aria-label="Nome do preço" data-testid={`price-name-${i}`} />
            <div className="mt-2 flex items-center gap-2">
              <input className="po-input min-h-[52px] w-28 shrink-0 text-lg" placeholder="R$ 0" inputMode="decimal" value={x.value} onChange={(e) => set(i, { value: e.target.value.replace(/[^\d.,]/g, "") })} aria-label="Valor em reais" data-testid={`price-value-${i}`} />
              <button type="button" onClick={() => set(i, { highlight: !x.highlight })} aria-pressed={x.highlight} data-testid={`price-highlight-${i}`}
                className={`min-h-[52px] flex-1 whitespace-nowrap rounded-full border-2 px-2 text-[15px] font-bold ${x.highlight ? "border-orange-500 bg-orange-500 text-white" : "border-slate-300 text-slate-700"}`}>
                {x.highlight ? "★ Preço destaque" : "☆ Preço destaque"}
              </button>
            </div>
          </div>
        ))}
      </div>
      {items.length < 5 && (
        <button type="button" onClick={() => setItems((l) => [...l, emptyPrice()])} className="po-btn-outline mt-3 w-full" data-testid="price-add">+ Adicionar preço</button>
      )}
      <button onClick={save} disabled={busy} className="po-btn-primary mt-3 w-full" data-testid="prices-save">{busy ? "Salvando..." : "Salvar preços"}</button>
    </div>
  );
}

function ProviderPanel({ user, refresh }) {
  const p = user.provider;
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: () => api.get("/provider/jobs").then((r) => r.data), refetchInterval: 15000 });
  const used = p.accepted_count || 0;
  const blocked = !p.is_pro && used >= p.free_limit;
  return (
    <div className="space-y-5">
      {p.status === "approved" ? <AvailabilityToggle provider={p} onChange={refresh} /> : (
        <div className={`rounded-3xl p-5 ${STATUS_MSG[p.status][2]}`} data-testid="provider-status-banner">
          <p className="flex items-center gap-2 text-xl font-bold"><Clock className="h-6 w-6" /> {STATUS_MSG[p.status][0]}</p>
          <p className="mt-1 text-lg">{STATUS_MSG[p.status][1]}</p>
        </div>
      )}
      <div className="po-card" data-testid="free-counter-card">
        {p.is_pro ? (
          <p className="flex items-center gap-2 text-xl font-bold text-orange-600"><Crown className="h-7 w-7" /> Plano Pro ativo até {new Date(p.pro_until).toLocaleDateString("pt-BR")}</p>
        ) : (
          <>
            <p className="text-xl font-bold text-slate-900" data-testid="free-counter-text">Pedidos grátis restantes: {Math.max(0, p.free_limit - used)} de {p.free_limit}</p>
            <div className="mt-3 h-4 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-orange-500" style={{ width: `${Math.min(100, (used / Math.max(1, p.free_limit)) * 100)}%` }} /></div>
            {blocked && <p className="mt-3 flex items-center gap-2 text-lg font-semibold text-red-700"><ShieldAlert className="h-6 w-6" /> Seus pedidos grátis acabaram. Assine o Pro para continuar recebendo.</p>}
            <Link to="/plano-pro" className="po-btn-primary mt-4 w-full" data-testid="go-pro-button"><Crown className="h-6 w-6" /> Assinar Plano Pro - R$ 11,99/mês</Link>
          </>
        )}
      </div>
      {p.status === "approved" && <DiscountControl provider={p} onSaved={refresh} />}
      {p.status === "approved" && <PricesEditor provider={p} onSaved={refresh} />}
      {p.status === "approved" && <PushAlert />}
      {p.status === "approved" && <PendingOffers />}
      {p.status === "approved" && <MissedOffers />}
      <div id="notification-setup"><NotificationSetup /></div>
      <div className="po-card">
        <p className="flex items-center gap-2 text-xl font-bold text-slate-900"><Briefcase className="h-6 w-6 text-orange-500" /> Meus atendimentos</p>
        {jobs.length === 0 && <p className="mt-2 text-lg text-slate-600">Os pedidos que você aceitar aparecem aqui.</p>}
        <div className="mt-3 space-y-3" data-testid="provider-jobs-list">{jobs.map((j) => <RequestRow key={j.id} r={j} />)}</div>
      </div>
      <Link to="/perfil/prestador" className="po-btn-outline w-full" data-testid="edit-provider-button"><Pencil className="h-5 w-5" /> Editar cadastro de prestador</Link>
    </div>
  );
}

function AccountForm({ user, refresh }) {
  const [f, setF] = useState({ name: user.name || "", whatsapp: user.whatsapp || "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/me", f); await refresh(); toast.success("Dados salvos!"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="po-card space-y-4" data-testid="account-form">
      <p className="text-xl font-bold text-slate-900">Meus dados</p>
      <p className="text-base text-slate-600">{user.email}</p>
      <div><label className="po-label" htmlFor="acc-name">Nome</label><input id="acc-name" className="po-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="account-name-input" /></div>
      <div><label className="po-label" htmlFor="acc-wa">WhatsApp</label><input id="acc-wa" type="tel" className="po-input" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="account-whatsapp-input" /></div>
      <button className="po-btn-dark w-full" data-testid="account-save-button">Salvar dados</button>
    </form>
  );
}

export default function Account() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-8 sm:px-6">
      <h1 className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">Olá, {user.name.split(" ")[0]}!</h1>
      {user.provider ? <ProviderPanel user={user} refresh={refresh} /> : (
        <div className="rounded-3xl bg-orange-500 p-6 text-white">
          <HardHat className="h-10 w-10" />
          <p className="mt-2 font-display text-2xl font-extrabold">Quer trabalhar como prestador?</p>
          <p className="mt-1 text-lg text-orange-50">Use esta mesma conta. Os 5 primeiros pedidos aceitos são grátis.</p>
          <Link to="/perfil/prestador" className="po-btn mt-4 w-full bg-white text-orange-600 hover:bg-orange-50" data-testid="become-provider-profile-button">Quero ser prestador</Link>
        </div>
      )}
      <AccountForm user={user} refresh={refresh} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Link to="/trocar-senha" className="po-btn-outline" data-testid="change-password-link"><KeyRound className="h-5 w-5" /> {user.has_password ? "Alterar senha" : "Criar senha"}</Link>
        <button onClick={async () => { await logout(); navigate("/"); }} className="po-btn-outline" data-testid="logout-button"><LogOut className="h-5 w-5" /> Sair</button>
      </div>
    </div>
  );
}
